import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import styles from './ProjectInfo.module.css'
import IconComponent from '../../../../IconComponent/IconComponent'
import { FaPencilRuler, FaUser, FaCalendar, FaShoppingCart } from 'react-icons/fa'
import { BsFillTagFill } from 'react-icons/bs'
import { Link } from 'react-router-dom'
import { sanityClient, urlFor } from '../../../../../lib/sanity.client'
import { ProjectInterface } from '../../../../../lib/types/project'
import { GoPlus } from 'react-icons/go'
import JumpingBar from '../../../../JumpingBar/JumpingBar'
import ProjectGallery from './ProjectGallery/ProjectGallery'
import TagMarquee from './TagMarquee/TagMarquee'
import { useTranslation } from '../../../../../TranslationProvider'
import { ProjectTypes } from '../../../../../lib/types/projectTypes'

export type ProjectInfoProps = {
    setIsFooterVisible: React.Dispatch<React.SetStateAction<boolean>>;
    setSearch: React.Dispatch<React.SetStateAction<string>>;
    setProjectTitle: React.Dispatch<React.SetStateAction<string>>;
    setHoverProject: React.Dispatch<React.SetStateAction<boolean>>;
}

const ProjectInfo = (props: ProjectInfoProps) => {

    const { currLang } = useTranslation(); // ✅ use context here
    const galleryRef = useRef<HTMLDivElement>(null);
    const successMessage = useRef<HTMLDivElement>(null);
    const [project, setProject] = useState<ProjectInterface | null>(null); // Fix here: store a single project
    const projectId = window.location.pathname.split("/").pop() || 0; // Get the project ID from the URL
    const [relatedProject, setRelatedProjects] = useState<ProjectInterface[]>([]);
    const [activeIndex, setActiveIndex] = useState(0);
    const palleteBox = useRef<(HTMLDivElement | null)>(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 1100);
    const [types, setTypes] = useState<ProjectTypes[]>([]);
    const { t } = useTranslation();


    
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 1100);
            if (window.innerWidth >= 1100) {
                setIsMobile(false);
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const mouseDownC = (index: number) => {
        setTimeout(() => {
            setActiveIndex(index);
        }, 200);
    }

    const openRelatedProject = (location: string) => {
        window.location.href = location; // This will force a page reload
    }

    useEffect(() => {
    const query = `*[
        _type == "project" &&
        titleUrl == $id
    ]{
        ...,
        typeProject[]->
    }`;

    const getRelatedProject = `*[
          _type == "project" &&
          titleUrl != $excluded_id &&
          (
            $types == 0 || count((typeProject[]->type[$lang])[@ in $types]) > 0
          )
        ]{
          ...,
          typeProject[]->
        }`;

    (sanityClient as any)
        .fetch(query, { id: projectId })
        .then((data: ProjectInterface[]) => {
            setProject(data[0]);

        return (sanityClient as any)
            .fetch(getRelatedProject, {
                types: data[0].typeProject.map((tp: any) => tp.type[currLang]),
                lang: currLang,
                excluded_id: projectId
            })
            .then((galleryData: any[]) => ({ galleryData }));
        })
        .then(({ galleryData }: { galleryData: any[] }) => {
            setRelatedProjects(galleryData);
        })
        .catch(console.error);
    }, [projectId]);


      const hexToCMYK = (hex: string): { c: number, m: number, y: number, k: number } => {
        // Remove the '#' from the HEX color if it exists
        hex = hex.replace(/^#/, '');
    
        // Convert HEX to RGB
        let r: number = parseInt(hex.substring(0, 2), 16);
        let g: number = parseInt(hex.substring(2, 4), 16);
        let b: number = parseInt(hex.substring(4, 6), 16);
    
        // Normalize RGB values to be between 0 and 1
        r /= 255;
        g /= 255;
        b /= 255;
    
        // Find the K value (black key)
        const k = 1 - Math.max(r, g, b);
    
        // If K is 1 (black), set CMY to 0
        if (k === 1) {
            return { c: 0, m: 0, y: 0, k: 1 };
        }
    
        // Calculate the C, M, Y values
        const c = (1 - r - k) / (1 - k);
        const m = (1 - g - k) / (1 - k);
        const y = (1 - b - k) / (1 - k);
    
        // Return CMYK values as percentages (0 to 100)
        return {
            c: +(c * 100).toFixed(2),
            m: +(m * 100).toFixed(2),
            y: +(y * 100).toFixed(2),
            k: +(k * 100).toFixed(2)
        };
    };
    
    const hexToRGB = (hex: string): { r: number, g: number, b: number } => {
        // Remove the '#' from the HEX color if it exists
        hex = hex.replace(/^#/, '');
    
        // Convert HEX to RGB by parsing the color components
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
    
        return { r, g, b };
    };

    const getHexColor = (hex: string, index: number) => {
        if(!isMobile){
            setActiveIndex(index);
            successMessage.current?.classList.remove(styles.visible)
            navigator.clipboard.writeText(hex)
                .then(() => {
                    successMessage.current?.classList.add(styles.visible)
                })
                .catch((err) => {
                    console.error('Failed to copy: ', err);
                });
        }
    }

    const hoverProject = (isHovering: boolean, title: string) => {
        if(isHovering){
            props.setHoverProject(true);
            props.setProjectTitle(title);
        }else{
            props.setHoverProject(false);
        }
    }

    useEffect(() => {
        props.setIsFooterVisible(true);
        window.scrollTo(0, 0);
    }, []);

    useLayoutEffect(() => {
        const gallery = galleryRef.current;
        if (!gallery) return;

        const handleWheel = (e: WheelEvent) => {
            const gallery = galleryRef.current;
            if (!gallery) return;

            const maxScrollLeft = gallery.scrollWidth - gallery.clientWidth;
            const atLeftEdge = gallery.scrollLeft === 0;
            const atRightEdge = gallery.scrollLeft >= maxScrollLeft;

            if (e.deltaY < 0 && atLeftEdge) {
                // Scrolling up but gallery is fully left — allow normal vertical scroll
                return;
            }

            if (e.deltaY > 0 && atRightEdge) {
                // Scrolling down but gallery fully right — allow normal vertical scroll
                return;
            }

            // Otherwise, scroll horizontally and block vertical scroll
            e.preventDefault();
            gallery.scrollLeft += e.deltaY;
        };

        gallery.addEventListener("wheel", handleWheel, { passive: false });

        return () => {
            gallery.removeEventListener("wheel", handleWheel);
        };
    }, [relatedProject]);


    return (
        <div className={styles.wrapper}>
            {project ? (
                <div>
            <div className={styles.section} id={styles.section_image}>
                <div className={styles.project_image} style={{backgroundImage: `url(${(urlFor(project.backgroundImage).url())})`}}>
                    <JumpingBar />
                </div>
            </div>
            <div className={styles.section} id={styles.section_project}>
                <div className={styles.project_info}>
                    <div className={styles.project_main}>
                        <div className={styles.client_info}>
                            <span className={styles.client_title}>{project.title}</span>
                            <span className={styles.client_info_tags}>
                                <div className={styles.tags}>
                                    {project.typeProject.map((type, index) => (
                                        <span key={index}>{type.type[currLang]}</span>
                                    ))}
                                </div>
                            </span>
                            <span className={styles.client_desc}>
                                {project.description[currLang]}
                            </span>
                            <div className={styles.client_about}>
                                <div className={styles.client_boxes}>
                                    <div className={styles.client_box} style={{backgroundColor: project.colorPallete ? "#" + project.colorPallete[0].hex : project.backgroundTheme ? "#" + project.backgroundTheme : "#000000"}}>
                                        <div className={styles.box_title}>
                                            <span style={{ color: "#" + project.themeTitle }}><IconComponent icon={FaUser} size={20} /></span>
                                            <span style={{ color: "#" + project.themeTitle }}>&nbsp;{t('client')}</span>
                                        </div>
                                        <span className={styles.box_desc} style={{ color: '#' + project.theme }}>{project.clientName}</span>
                                    </div>
                                    <div className={styles.client_box} style={{backgroundColor: project.colorPallete ? "#" + project.colorPallete[0].hex : project.backgroundTheme ? "#" + project.backgroundTheme : "#000000"}}>
                                        <div className={styles.box_title}>
                                            <span style={{ color: "#" + project.themeTitle }}><IconComponent icon={FaCalendar} size={20} /></span>
                                            <span style={{ color: "#" + project.themeTitle }}>&nbsp;{t('timeline')}</span>
                                        </div>
                                        <span className={styles.box_desc} style={{ color: '#' + project.theme }}>{project.timeline} {t('weeks')}</span>
                                    </div>
                                </div>
                                <div className={styles.client_boxes}>
                                    <div className={styles.client_box} style={{backgroundColor: project.colorPallete ? "#" + project.colorPallete[0].hex : project.backgroundTheme ? "#" + project.backgroundTheme : "#000000"}}>
                                        <div className={styles.box_title}>
                                            <span style={{ color: "#" + project.themeTitle }}><IconComponent icon={FaPencilRuler} size={20} /></span>
                                            <span style={{ color: "#" + project.themeTitle }}>&nbsp;{t('services')}</span>
                                        </div>
                                        <span className={styles.box_desc} style={{ color: '#' + project.theme }}>
                                            {project.services.map((type, index) => (
                                                `${type[currLang]}` + (index < project.services.length - 1 ? ', ' : '') 
                                            ))}
                                        </span>
                                    </div>
                                    <div className={styles.client_box} style={{backgroundColor: project.colorPallete ? "#" + project.colorPallete[0].hex : project.backgroundTheme ? "#" + project.backgroundTheme : "#000000"}}>
                                        <div className={styles.box_title}>
                                            <span style={{ color: "#" + project.themeTitle }}><IconComponent icon={FaShoppingCart} size={20} /></span>
                                            <span style={{ color: "#" + project.themeTitle }}>&nbsp;{t('deliverables')}</span>
                                        </div>
                                        <span className={styles.box_desc} style={{ color: '#' + project.theme }}>
                                            {project.deliverables.map((type, index) => (
                                                `${type[currLang]}` + (index < project.deliverables.length - 1 ? ', ' : '') 
                                            ))}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className={`${styles.client_info2} ${styles.client_info2_border}`}>
                            <div className={styles.client_image} style={{backgroundImage: `url(${(urlFor(project.image).url())})`}}></div>
                        </div>
                    </div>
                    <div className={styles.project_main}>
                        <div className={styles.client_info}>
                            <div className={styles.client_tags}>
                                <div className={styles.tags_title}>
                                    <span><IconComponent icon={BsFillTagFill} size={21} /></span>
                                    <span>&nbsp;Tags</span>
                                </div>
                                <div className={styles.tags}>
                                    {project.typeProject.map((type, index) => (
                                        <span key={index}>{type.type[currLang]}</span>
                                    ))}
                                </div>
                            </div>
                            <div className={styles.client_comment} style={{backgroundColor: project.colorPallete ? "#" + project.colorPallete[0].hex : project.backgroundTheme ? "#" + project.backgroundTheme : "#000000"}}>
                                <div className={styles.comment_body}>
                                    <span style={{ color: '#' + project.theme }}>{project.clientComment[currLang].toString()}</span>
                                </div>
                                <div className={styles.client_footer}>
                                    <span className={styles.client_name} style={{ color: '#' + project.theme }}>{project.clientName}</span>
                                    <span className={styles.client_position} style={{ color: '#' + project.theme }}>{project.clientPosition[currLang]}, {project.title}</span>
                                </div>
                            </div>
                        </div>
                            <div className={styles.client_info2}>
                                {project.colorPallete && (
                                    <div className={styles.client_pallete}>
                                        <span className={styles.pallete_title}>{t('color_pallete')}</span>
                                        <div className={styles.pallete_scheme}>
                                            {project.colorPallete.map((color, index) => (
                                                <div 
                                                    onClick={() => getHexColor(color.hex, index)} 
                                                    className={`${styles.pallete_box} 
                                                    ${index > 0 ? styles.pallete_box_left : ''}
                                                    ${activeIndex === index ? styles.active_pallete_box : ''}`} 
                                                    style={{zIndex: index, backgroundColor: "#" + color.hex, color: "#" + color.backgroundColor}} 
                                                    key={index}
                                                    onMouseEnter={() => mouseDownC(index)}
                                                    onMouseLeave={() => mouseDownC(index)}
                                                    ref={palleteBox}
                                                >
                                                    <div className={styles.pallete_header}>
                                                        <span className={styles.pallete_color_name}>{color.name[currLang]}</span>
                                                        <span className={styles.pallete_hex}>#{color.hex}</span>
                                                    </div>
                                                    <div className={styles.pallete_footer}>
                                                        <div className={styles.colors_info}>
                                                            <span>C: {hexToCMYK(color.hex).c}</span>
                                                            <span>M: {hexToCMYK(color.hex).m}</span>
                                                            <span>Y: {hexToCMYK(color.hex).y}</span>
                                                            <span>K: {hexToCMYK(color.hex).k}</span>
                                                        </div>
                                                        <div className={styles.colors_info}>
                                                            <span>R: {hexToRGB(color.hex).r}</span>
                                                            <span>G: {hexToRGB(color.hex).g}</span>
                                                            <span>B: {hexToRGB(color.hex).b}</span>
                                                        </div>
                                                    </div>
                                                    <div className={styles.pallete_expand} style={{ color: "#" + color.backgroundColor }}>
                                                        <IconComponent icon={GoPlus} size={35} />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                <Link to={"/about#contact"} className={styles.client_contact} style={{backgroundColor: project.colorPallete ? "#" + project.colorPallete[0].hex : project.backgroundTheme ? "#" + project.backgroundTheme : "#000000"}}>
                                    <span style={{ color: '#' + project.theme }}>{t('lets_talk')}</span>
                                </Link>
                            </div>
                    </div>
                </div>
            </div>
            <div className={styles.section2}>
                <ProjectGallery id={project._id} />
            </div>
            {relatedProject && relatedProject[0] && (
            <div className={styles.project_gallery}>
                <div className={styles.project_gallery_header}>
                    <div className={styles.project_gallery_titles}>
                        <span className={styles.project_gallery_header_title}>{t('related_projects')}</span>
                    </div>
                </div>
                <div 
                    className={styles.project_gallery_content} 
                    ref={galleryRef}
                >
                    {relatedProject.map((type, index) => (
                            <div 
                                className={styles.related_projects_project} 
                                style={{backgroundImage: `url(${(urlFor(type.backgroundImage).url())})`}}
                                onClick={() => openRelatedProject(`/projects/${type.titleUrl}`)}
                                key={index}
                                onMouseEnter={() => hoverProject(true, type.title)}
                                onMouseLeave={() => hoverProject(false, '')}
                            >
                                <TagMarquee 
                                    tags={type.typeProject.map(t => t.type[currLang])} 
                                    speed={5} 
                                />
                            </div>
                    ))}
                </div>
            </div>
            )}
            <div className={styles.success} ref={successMessage}>
                <span>{t('text_copied')}</span>
            </div>
            </div>
            ) : (
                <div className={styles.section} id={styles.section_project}>{t('project_notfound')}</div>
            )}
        </div>
    )
}

export default ProjectInfo
